import { MousePointerClick } from '@tamagui/lucide-icons-2'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { View } from 'tamagui'
import { waitForSelector, Z_INDEX } from './constants'
import { useOnboarding } from './OnboardingContext'

interface HighlightRect {
    tourId: string
    top: number
    left: number
    width: number
    height: number
}

// Inject pulse keyframes once
if (typeof document !== 'undefined') {
    const styleId = 'onboarding-pulse-style'
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `
            @keyframes onboarding-pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.3); opacity: 0.7; }
            }
        `
        document.head.appendChild(style)
    }
}

export default function OnboardingHighlight() {
    const { tourHighlights, isInTourStepFlow, exitTourStepFlow } = useOnboarding()
    const [rects, setRects] = useState<HighlightRect[]>([])

    const calculateRects = useCallback(() => {
        if (!tourHighlights.length) {
            setRects([])
            return
        }

        const newRects: HighlightRect[] = []
        for (const h of tourHighlights) {
            const el = document.querySelector(`[data-tour="${h.tourId}"]`)
            if (el) {
                const rect = el.getBoundingClientRect()
                newRects.push({
                    tourId: h.tourId,
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                })
            }
        }
        setRects(newRects)
    }, [tourHighlights])

    // Scroll highlighted elements into view only when highlights change (not on scroll)
    useEffect(() => {
        for (const h of tourHighlights) {
            const el = document.querySelector(`[data-tour="${h.tourId}"]`)
            if (el) {
                const rect = el.getBoundingClientRect()
                if (rect.top < 0 || rect.bottom > window.innerHeight) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            }
        }
    }, [tourHighlights])

    // Recalculate on highlights change, resize, scroll (rAF-throttled)
    const rafRef = useRef(0)
    const throttledCalculateRects = useCallback(() => {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = requestAnimationFrame(calculateRects)
    }, [calculateRects])

    useEffect(() => {
        calculateRects()
        const timeout = setTimeout(calculateRects, 500)

        window.addEventListener('resize', throttledCalculateRects)
        window.addEventListener('scroll', throttledCalculateRects, true)

        return () => {
            clearTimeout(timeout)
            cancelAnimationFrame(rafRef.current)
            window.removeEventListener('resize', throttledCalculateRects)
            window.removeEventListener('scroll', throttledCalculateRects, true)
        }
    }, [calculateRects, throttledCalculateRects])

    // Retry finding element when highlights change but element not yet in DOM
    // Safety: if element not found after 5s, exit the tour to avoid stuck mask
    useEffect(() => {
        if (!tourHighlights.length) return

        let cancelled = false
        for (const h of tourHighlights) {
            const el = document.querySelector(`[data-tour="${h.tourId}"]`)
            if (!el) {
                waitForSelector(`[data-tour="${h.tourId}"]`, 5000).then((found) => {
                    if (cancelled) return
                    if (found) {
                        calculateRects()
                    } else {
                        exitTourStepFlow()
                    }
                })
            }
        }
        return () => { cancelled = true }
    }, [tourHighlights, calculateRects, exitTourStepFlow])

    if (!isInTourStepFlow || !tourHighlights.length || !rects.length) return null

    // Build clip-path with evenodd: full-screen rect + cutout holes
    const W = window.innerWidth
    const H = window.innerHeight
    const pad = 4
    const cutoutPaths = rects.map(r => {
        const x1 = r.left - pad
        const y1 = r.top - pad
        const x2 = r.left + r.width + pad
        const y2 = r.top + r.height + pad
        return `M${x1},${y1} H${x2} V${y2} H${x1} Z`
    }).join(' ')
    const clipPath = `path(evenodd, "M0,0 H${W} V${H} H0 Z ${cutoutPaths}")`

    // Render via portal so it sits in the same stacking context as Dialog.Portal
    return createPortal(
        <View
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={Z_INDEX.HIGHLIGHT}
            // @ts-ignore - web style
            style={{ pointerEvents: 'none' }}
        >
            {/* Click blocker with holes at cutout positions */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'auto',
                    cursor: 'not-allowed',
                    clipPath,
                }}
                onClick={e => { e.stopPropagation(); e.preventDefault() }}
            />

            {/* Semi-transparent overlay with cutouts */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                }}
            >
                <defs>
                    <mask id="onboarding-spotlight-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {rects.map(r => (
                            <rect
                                key={r.tourId}
                                x={r.left - pad}
                                y={r.top - pad}
                                width={r.width + pad * 2}
                                height={r.height + pad * 2}
                                rx="8"
                                fill="black"
                            />
                        ))}
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.5)"
                    mask="url(#onboarding-spotlight-mask)"
                />
            </svg>

            {/* Visual-only cutout indicators with pulsing click icon */}
            {rects.map(r => (
                <div
                    key={`cutout-${r.tourId}`}
                    style={{
                        position: 'absolute',
                        top: r.top - pad,
                        left: r.left - pad,
                        width: r.width + pad * 2,
                        height: r.height + pad * 2,
                        borderRadius: 8,
                        pointerEvents: 'none',
                        zIndex: Z_INDEX.HIGHLIGHT_CUTOUT,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            pointerEvents: 'none',
                            animation: 'onboarding-pulse 1.5s ease-in-out infinite',
                            zIndex: Z_INDEX.HIGHLIGHT_ICON,
                        }}
                    >
                        <MousePointerClick size={36} color="$statusOk" />
                    </div>
                </div>
            ))}
        </View>,
        document.body,
    )
}
